import React, { useContext, useState, useEffect, useCallback } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { IFigureDto } from '../models/IFigureDto';
import { FavoritesContext } from '../context/FavoritesContext';
import Tags from './Tags';
import { MoreVertMenu } from './MoreVertMenu';
import { StatusChip, StatusCode } from './StatusChip';
import DataDetails from './DataDetails';
import { formatNorwegianDate, sanitizeHtml } from '../utils/formatters';
import missingImg from '../assets/not_found.png';
import CollectionAvatars from './CollectionAvatars';
import Series from './Series';
import { useAppEvent } from '../hooks/useAppEvent';

interface CardDataProps {
  figure: IFigureDto;
  collectionId: number;
  onFigureSelect: (figure: IFigureDto) => void;
  onAvatarClick: (collectionId: number) => void;
  collections?: Array<{ id: number; name: string; color?: string }>;
  isFavoritesCollection?: boolean;
  isDefaultCollection?: boolean;
}

const CardData: React.FC<CardDataProps> = ({ figure: initialFigure, collectionId, onFigureSelect, onAvatarClick, collections = [], isFavoritesCollection = false, isDefaultCollection = false }) => {
  const { toggleFavorite, isFavorite } = useContext(FavoritesContext);
  const [figure, setFigure] = useState(initialFigure);

  // Use FavoritesContext to reflect global favorite state
  const isFav = isFavorite(figure.imageId.toString());

  useAppEvent('figureUpdated', (updatedFigure) => {
    if (updatedFigure.imageId !== figure.imageId) return;
    // eslint-disable-next-line no-console
    console.log(`CardData: Updated figure ${updatedFigure.imageId}, customerFavorite=${updatedFigure.customerFavorite}`);
    setFigure(updatedFigure);
  });

  useAppEvent('figureRemoved', ({ removedFigureId, collectionId: removedFrom }) => {
    if (removedFigureId !== figure.imageId) return;
    setFigure((prev) => {
      const next = { ...prev, collectionMemberships: { ...prev.collectionMemberships } };
      delete next.collectionMemberships[Number(removedFrom)];
      return next;
    });
  });

  // Update figure when props change (only update when the incoming id differs)
  useEffect(() => {
    setFigure((prev) => (prev.imageId !== initialFigure.imageId ? initialFigure : prev));
  }, [initialFigure]);

  const safeStatus = [0, 1, 2, 3, 4].includes(figure.status) ? (figure.status as StatusCode) : (0 as StatusCode);

  const hasZoomFileExt = typeof figure.zoomFile === 'string' && /\.[^/.]+$/.test(figure.zoomFile);
  const imageSrc = hasZoomFileExt ? `/image?docNumber=${figure.docNumber}&versionId=${figure.versionId}&fileName=${figure.zoomFile}` : missingImg;

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      console.debug(`CardData favorite click: imageId=${figure.imageId}, isFav=${isFav}, collectionId=${collectionId}`);
      await toggleFavorite(figure.imageId.toString(), collectionId);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent clicks on interactive elements from triggering card navigation
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input')) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onFigureSelect(figure);
  }, [figure, onFigureSelect]);

  return (
    <Card
      onClick={handleClick}
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        outline: 'none',
        bgcolor: 'background.paper',
        transition: 'box-shadow 0.2s',
        boxSizing: 'border-box',
        m: 0,
        flex: 1,
        alignSelf: 'flex-start',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 3,
        },
      }}>
      <Box
        sx={{
          position: 'relative',
          //paddingTop: figure.isAiSearchResult ? 0 : '40%',
          paddingTop: '40%',
          overflow: 'hidden',
          maxWidth: '100%',
          m: 0,
          //flexGrow: figure.isAiSearchResult ? 1 : 0,
          //height: figure.isAiSearchResult ? '100%' : undefined,
          flexGrow: 0,
          height: undefined,
        }}>
        <img
          //src={`/image?docNumber=${figure.docNumber}&versionId=${figure.versionId}&fileName=${figure.zoomFile}`}
          src={imageSrc}
          title={`imageId: ${figure.imageId}`}
          alt={figure.imgName || ''}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            margin: 'auto',
            maxWidth: '100%',
            maxHeight: '100%',
            //width: figure.isAiSearchResult ? '100%' : 'auto',
            //height: figure.isAiSearchResult ? '100%' : 'auto',
            //objectFit: figure.isAiSearchResult ? 'cover' : 'contain',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
          }}
        />
      </Box>

      <CardContent
        sx={{
          flexGrow: 1,
          p: 2,
          '&:last-child': { pb: 2 },
          m: 0,
          bgcolor: '#efefef', // Light grey background
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 0.5,
            gap: 0.5,
          }}>
          <IconButton
            size="small"
            sx={{
              mr: 0.5,
              padding: '2px',
            }}>
            {/* <NoteAddIcon fontSize="small" /> */}
            <Series seriesId={figure.seriesId} size={20} />
          </IconButton>

          <Typography
            variant="body1"
            sx={{
              mr: 0.5,
              minWidth: 'auto',
              fontSize: '0.8rem',
              fontWeight: 700,
            }}>
            {figure.imgName}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />
          <StatusChip
            status={safeStatus}
            showLabel={false}
            lastUpdated={formatNorwegianDate(figure.publishedDate?.toString())}
            published={formatNorwegianDate(figure.createdDate?.toString())}
            description="Some text here please - from where?"
          />

          <IconButton
            size="small"
            onClick={handleFavoriteClick}
            sx={{
              color: isFav ? 'error.main' : 'action.active',
              padding: '2px',
            }}>
            {isFav ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
          </IconButton>

          <MoreVertMenu
            onEdit={() => onFigureSelect(figure)}
            onHelp={() => {}}
            customerId={1}
            figureId={figure.imageId}
            collectionId={collectionId}
          />
        </Box>

        <Typography
          variant="body2"
          color="text.primary"
          sx={{
            mb: 0.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '0.75rem',
          }}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(figure.captionHtml ?? ''),
          }}
        />
        <Typography
          variant="body2"
          color="text.primary"
          sx={{
            mb: 0.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '1.0rem',
          }}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(figure.context ?? ''),
          }}
        />
        <DataDetails
          docNumber={figure.docNumber || ''}
          guideVersionPublicId={figure.guideVersionPublicId ?? ''}
          publishedDate={figure.publishedDate?.toString()}
          imgName={figure.imgName}
        />

        {!isFavoritesCollection && (
          <>
            {/* {figure.collectionMemberships && Object.keys(figure.collectionMemberships).length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }} noWrap>
                  {Object.values(figure.collectionMemberships).join(', ')}
                </Typography>
              </Box>
            )} */}

            {isDefaultCollection && (
              <CollectionAvatars
                collections={Object.entries(figure.collectionMemberships).map(([id, name]) => {
                  const collectionId = Number(id);
                  const collection = collections.find((c) => c.id === collectionId);
                  return { id, name, color: collection?.color };
                })}
                variant="medium"
                onAvatarClick={onAvatarClick}
                onOverflowClick={() => onFigureSelect(figure)}
              />
            )}

            <Box sx={{ mt: 'auto' }}>
              <Tags imageData={figure} showRemovableTags={!isDefaultCollection} collectionId={collectionId} onOverflowClick={() => onFigureSelect(figure)} collections={collections} hideAddTag={isDefaultCollection} showAllTags={isDefaultCollection} />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CardData;
